"use client";

import { useState, type ReactNode } from "react";

// Generic, reusable "collapsible dev-only debug panel" chrome -- same interaction pattern as Next.js's
// own dev indicator (a small round badge bottom-corner, click to expand a full panel, click again to
// collapse back down). Not tied to any one data source: DeviceCapabilityDebug's content lives inside
// this as children today, and any future debug data (server-side, other UI state) can either add more
// content to that same instance or mount a second instance of this same shell -- the toggle/expand
// mechanism itself only needs to be built once.
//
// Starts collapsed on every mount -- no persistence across reloads/navigations, matching how a dev tool
// indicator is expected to behave (a quiet badge by default, not a panel that stays pinned open).
export function DevDebugWidget({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        className="dev-debug-widget-toggle"
        onClick={() => setOpen(true)}
        aria-label={`Show ${label}`}
        title={label}
      >
        DBG
      </button>
    );
  }

  return (
    <div className="dev-debug-widget-panel" role="dialog" aria-label={label}>
      <div className="dev-debug-widget-header">
        <span>{label}</span>
        <button type="button" className="dev-debug-widget-close" onClick={() => setOpen(false)} aria-label="Minimize">
          ×
        </button>
      </div>
      <div className="dev-debug-widget-body">{children}</div>
    </div>
  );
}
